import { put, call, takeLatest } from "redux-saga/effects";
import * as API from "services";
import * as Actions from "actions";
import { TemplatesHistory } from "constants/Actions";
import { i18n } from "us.helper";
import { $Message, $MessageBox } from "us.common/components";
import _ from "lodash";
import {
  getTemplateAdapterIds,
  groupByGroupId,
  updateVersions,
} from "functions";
import { getSortedTemplateData } from "components/EditorHome/Components/TemplateContent/Functions";
import { SortingOptions, SortingOrder } from "components/EditorHome/Constants";

const { templatesHistory, templateEditor } = Actions;
const { parameterList } = API.templateEditor;
const { templates } = API.templatesHistory;

const {
  GET_TEMPLATES,
  GET_TEMPLATE_DATA,
  GET_DOCUMENT_TYPES,
  SAVE_TEMPLATE_DATA,
  COPY_TEMPLATE,
  GET_TEMPLATE_VERSIONS_HISTORY,
} = TemplatesHistory;

const TemplatesHistorySagas = {
  templates: {
    search: function* (action: any) {
      try {
        const params = action?.payload.data ?? {};
        const { data, status } = yield call(templates.get, params);
        if (Array.isArray(data) && status === 200) {
          yield put(
            templatesHistory.templates.success({
              templates: getSortedTemplateData(
                data,
                SortingOptions.LAST_UPDATED_DATE,
                SortingOrder.DESC
              ),
              groups: groupByGroupId(
                getSortedTemplateData(
                  data,
                  SortingOptions.LAST_UPDATED_DATE,
                  SortingOrder.DESC
                )
              ),
            })
          );
        } else {
          yield put(
            templatesHistory.templates.success({ templates: [], groups: [] })
          );
        }
      } catch (error) {
        yield put(templatesHistory.templates.fail([]));
      }
    },
    documentTypes: function* () {
      try {
        const { data, status } = yield call(templates.getDocumentTypes);
        if (Array.isArray(data) && status === 200) {
          yield put(templatesHistory.documentTypes.success(data));
        } else {
          yield put(templatesHistory.documentTypes.success([]));
        }
      } catch (error) {
        yield put(templatesHistory.documentTypes.fail([]));
      }
    },
  },
  template: {
    get: function* (action: any) {
      try {
        const params = action?.payload.data ?? {};
        const { data, status } = yield call(templates.getById, params);
        if (status === 200) {
          yield put(templatesHistory.template.success(data));
          const adapterIds = getTemplateAdapterIds({
            template: data,
            all: true,
          });
          try {
            const { data: parameters, status } = yield call(
              parameterList.get,
              adapterIds
            );
            if (Array.isArray(parameters) && status === 200) {
              yield put(templateEditor.parameters.success(parameters));
            } else {
              yield put(templateEditor.parameters.success([]));
            }
          } catch (error) {
            yield put(templateEditor.parameters.fail([]));
          }
        } else {
          yield put(templatesHistory.template.fail({}));
        }
      } catch (error) {
        yield put(templatesHistory.template.fail({}));
      }
    },
    save: function* (action: any) {
      try {
        $Message.loading(i18n.t("COMMON.SAVING") as string, 0);
        let { saveParams, isSaveAs } = action?.payload.data ?? {};
        if(saveParams.templateId <= 0){
          saveParams = {
            ...saveParams,
            mainAdapterId:
              saveParams?.mainAdapterId == 0
                ? saveParams?.templateContent?.adapter?.mainAdapterId
                : saveParams?.mainAdapterId,
            templateContent: {
              ...saveParams?.templateContent,
              versionNo: 0,
              adapter: {
                ...saveParams?.templateContent.adapter,
              },
            },
          };
        }
        if(saveParams?.templateId > 0) {
          try {
            const { data, status } = yield call(templates.getHistory, {
              templateId: saveParams?.templateId,
            });
            if (Array.isArray(data) && (status === 200 || status === 204)) {
              const versionList = updateVersions(data);
              if (versionList.length > 0) {
                saveParams = {
                  ...saveParams,
                  templateContent: {
                    ...saveParams?.templateContent,
                    versionNo: versionList[0]?.versionNo + 1,
                  },
                };
              }
              yield put(templatesHistory.templateVersions.success(versionList));
            } else {
              yield put(templatesHistory.templateVersions.fail([]));
            }
          } catch (error) {
            yield put(templatesHistory.templateVersions.fail([]));
          }
        }
        const { data, status } = yield call(templates.save, saveParams);
        $Message.destroy();
        if (status === 200) {
          yield put(
            templatesHistory.templateData.success({
              saveParams: saveParams,
              templateId: data,
              isSaveAs,
            })
          );
          if (saveParams.templateContent?.versionNo == 0) {
            $MessageBox(
              "success",
              `US.USC.TEMPLATEEDITOR:MESSAGES.TEMPLATE_SAVED_SUCCESSFULLY`,
              "",
              ""
            );
          } else {
            $MessageBox(
              "success",
              `US.USC.TEMPLATEEDITOR:MESSAGES.TEMPLATE_SAVED_SUCCESSFULLY_WILL_BE_ABLE_TO_SEE_IN_THE_VERSION_HISTORY_SOON`,
              "",
              ""
            );
          }
        } else if (status === 400 && data?.code == 400) {
          yield put(
            templatesHistory.templateData.fail({ saveParams: saveParams })
          );
          $MessageBox(
            "error",
            `US.USC.TEMPLATEEDITOR:MESSAGES.TEMPLATE_NAME_ALREADY_EXISTS`,
            "",
            ""
          );
        } else {
          yield put(
            templatesHistory.templateData.fail({ saveParams: saveParams })
          );
          $MessageBox(
            "error",
            `US.USC.TEMPLATEEDITOR:MESSAGES.TEMPLATE_SAVE_FAILED`,
            "",
            ""
          );
        }
      } catch (error) {
        $Message.destroy();
        yield put(
          templatesHistory.templateData.fail({
            saveParams: action?.payload.data?.saveParams,
          })
        );
        $MessageBox(
          "error",
          `US.USC.TEMPLATEEDITOR:MESSAGES.TEMPLATE_SAVE_FAILED`,
          "",
          ""
        );
      }
    },
    copy: function* (action: any) {
      try {
        $Message.loading(i18n.t("COMMON.COPYING") as string, 0);
        const params = action?.payload.data ?? {};
        const { data, status } = yield call(templates.getById, params);
        if (status === 200) {
          yield put(templatesHistory.templateCopy.success(data));
        } else {
          yield put(templatesHistory.templateCopy.fail({}));
        }
      } catch (error) {
        yield put(templatesHistory.templateCopy.fail({}));
      } finally {
        $Message.destroy();
      }
    },
    getHistory: function* (action: any) {
      try {
        const params = action?.payload.data ?? {};
        const { data, status } = yield call(templates.getHistory, params);
        if (Array.isArray(data) && (status === 200 || status === 204)) {
          yield put(
            templatesHistory.templateVersions.success(updateVersions(data))
          );
        } else {
          yield put(templatesHistory.templateVersions.fail([]));
        }
      } catch (error) {
        yield put(templatesHistory.templateVersions.fail([]));
      }
    },
  },
};

export default [
  takeLatest(GET_TEMPLATES, TemplatesHistorySagas.templates.search),
  takeLatest(GET_TEMPLATE_DATA, TemplatesHistorySagas.template.get),
  takeLatest(GET_DOCUMENT_TYPES, TemplatesHistorySagas.templates.documentTypes),
  takeLatest(SAVE_TEMPLATE_DATA, TemplatesHistorySagas.template.save),
  takeLatest(COPY_TEMPLATE, TemplatesHistorySagas.template.copy),
  takeLatest(
    GET_TEMPLATE_VERSIONS_HISTORY,
    TemplatesHistorySagas.template.getHistory
  ),
];
